import React, { useState } from 'react';
import { Key, Search, Eye, EyeOff, Filter, Lock, MoreHorizontal, Plus } from 'lucide-react';
import { Credencial } from '../../types';

interface CredentialManagerProps {
  credenciales: Credencial[];
}

const CredentialManager: React.FC<CredentialManagerProps> = ({ credenciales }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<string | null>(null);
  const [credencialSeleccionada, setCredencialSeleccionada] = useState<Credencial | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Filtrado de credenciales
  const credencialesFiltradas = credenciales.filter(cred => {
    const matchesSearch = cred.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          cred.usuario.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filtroTipo ? cred.tipo === filtroTipo : true;
    return matchesSearch && matchesTipo;
  });

  // Determinar el color según el tipo de credencial
  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'bancaria':
        return 'text-cyber-blue bg-cyber-blue/10 border-cyber-blue/30';
      case 'social':
        return 'text-cyber-warning bg-cyber-warning/10 border-cyber-warning/30';
      case 'empresarial':
        return 'text-cyber-safe bg-cyber-safe/10 border-cyber-safe/30';
      default:
        return 'text-cyber-light bg-cyber-gray/10 border-cyber-gray/30';
    }
  };

  // Determinar el nombre del tipo en español
  const getTipoNombre = (tipo: string) => {
    switch (tipo) {
      case 'bancaria': return 'Bancaria';
      case 'social': return 'Social';
      case 'empresarial': return 'Empresarial';
      default: return tipo;
    }
  };

  return (
    <div className="bg-cyber-dark-blue bg-opacity-80 backdrop-blur-md p-4 rounded-lg border border-cyber-blue/30">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <Key className="h-5 w-5 mr-2 text-cyber-blue" />
          Gestor de Credenciales
        </h3>
        
        <button className="p-1.5 bg-cyber-blue/20 hover:bg-cyber-blue/30 rounded-md transition-colors">
          <Plus className="h-4 w-4 text-cyber-blue" />
        </button>
      </div>
      
      {/* Búsqueda y filtros */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-cyber-light" />
          </div>
          <input
            type="text"
            placeholder="Buscar credencial..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-cyber-gray/30 text-white placeholder-gray-400 block w-full pl-10 pr-3 py-1.5 text-sm border border-cyber-blue/30 rounded-md focus:outline-none focus:ring-1 focus:ring-cyber-blue"
          />
        </div>
        
        <div className="flex gap-2">
          <button 
            onClick={() => setFiltroTipo(filtroTipo === 'bancaria' ? null : 'bancaria')}
            className={`px-3 py-1.5 text-xs rounded-md border ${
              filtroTipo === 'bancaria' 
                ? 'bg-cyber-blue/20 border-cyber-blue/50 text-cyber-blue' 
                : 'bg-cyber-gray/20 border-cyber-gray/50 text-cyber-light hover:bg-cyber-gray/30'
            } transition-colors`}
          >
            Bancarias
          </button>
          
          <button 
            onClick={() => setFiltroTipo(filtroTipo === 'social' ? null : 'social')}
            className={`px-3 py-1.5 text-xs rounded-md border ${
              filtroTipo === 'social' 
                ? 'bg-cyber-warning/20 border-cyber-warning/50 text-cyber-warning' 
                : 'bg-cyber-gray/20 border-cyber-gray/50 text-cyber-light hover:bg-cyber-gray/30'
            } transition-colors`}
          >
            Sociales
          </button>
          
          <button 
            onClick={() => setFiltroTipo(filtroTipo === 'empresarial' ? null : 'empresarial')}
            className={`px-3 py-1.5 text-xs rounded-md border ${
              filtroTipo === 'empresarial' 
                ? 'bg-cyber-safe/20 border-cyber-safe/50 text-cyber-safe' 
                : 'bg-cyber-gray/20 border-cyber-gray/50 text-cyber-light hover:bg-cyber-gray/30'
            } transition-colors`}
          >
            Empresariales
          </button>
          
          {filtroTipo && (
            <button 
              onClick={() => setFiltroTipo(null)}
              className="p-1.5 text-xs rounded-md bg-cyber-gray/20 border border-cyber-gray/50 text-cyber-light hover:bg-cyber-gray/30 transition-colors"
              title="Limpiar filtros"
            >
              <Filter className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Lista de credenciales */}
      <div className="grid gap-3 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
        {credencialesFiltradas.length > 0 ? (
          credencialesFiltradas.map((credencial) => (
            <div 
              key={credencial.id}
              className={`p-3 rounded-lg border ${
                credencialSeleccionada?.id === credencial.id 
                  ? 'border-cyber-blue bg-cyber-blue/10' 
                  : 'border-cyber-gray/30 bg-cyber-gray/10 hover:bg-cyber-gray/20'
              } transition-all duration-200 cursor-pointer`}
              onClick={() => setCredencialSeleccionada(credencial)}
            >
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-medium text-white">{credencial.nombre}</h4>
                
                <div className="flex gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${getTipoColor(credencial.tipo)}`}>
                    {getTipoNombre(credencial.tipo)}
                  </span>
                  
                  <span className="text-xs px-2 py-0.5 rounded-full bg-cyber-gray/20 border border-cyber-gray/50 text-cyber-light">
                    Nivel {credencial.nivelAcceso}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <p className="text-sm text-cyber-light">{credencial.usuario}</p>
                <div className="flex gap-2">
                  {credencialSeleccionada?.id === credencial.id && (
                    <>
                      <button className="p-1 rounded-full bg-cyber-gray/30 hover:bg-cyber-gray/50 transition-colors">
                        <Lock className="h-3.5 w-3.5 text-cyber-blue" />
                      </button>
                      <button className="p-1 rounded-full bg-cyber-gray/30 hover:bg-cyber-gray/50 transition-colors">
                        <MoreHorizontal className="h-3.5 w-3.5 text-cyber-light" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-cyber-light">
            <p>No se encontraron credenciales</p>
          </div>
        )}
      </div>
      
      {/* Detalle de credencial seleccionada */}
      {credencialSeleccionada && (
        <div className="mt-4 p-3 rounded-lg border border-cyber-blue/30 bg-cyber-blue/5">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-sm font-medium text-white">{credencialSeleccionada.nombre}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${getTipoColor(credencialSeleccionada.tipo)}`}>
              {getTipoNombre(credencialSeleccionada.tipo)}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mb-3">
            <div>
              <p className="text-xs text-cyber-light">Usuario</p>
              <p className="text-white">{credencialSeleccionada.usuario}</p>
            </div>
            
            <div>
              <p className="text-xs text-cyber-light">Contraseña</p>
              <div className="flex items-center gap-2">
                <p className="text-white">{showPassword ? '••••••••••••' : '••••••••••••'}</p>
                <button 
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 rounded-full bg-cyber-gray/30 hover:bg-cyber-gray/50 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-3.5 w-3.5 text-cyber-light" />
                  ) : (
                    <Eye className="h-3.5 w-3.5 text-cyber-light" />
                  )}
                </button>
              </div>
            </div>
            
            <div>
              <p className="text-xs text-cyber-light">Nivel de Acceso</p>
              <p className="text-white">{credencialSeleccionada.nivelAcceso}</p>
            </div>
            
            <div>
              <p className="text-xs text-cyber-light">Última Actualización</p>
              <p className="text-white">
                {credencialSeleccionada.fechaActualizacion.toLocaleDateString('es-ES')}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-2">
            <button className="px-3 py-1 text-xs rounded-md bg-cyber-gray/30 text-cyber-light hover:bg-cyber-gray/50 transition-colors">
              Editar
            </button>
            <button className="px-3 py-1 text-xs rounded-md bg-cyber-blue/20 text-cyber-blue hover:bg-cyber-blue/30 transition-colors">
              Gestionar Acceso
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CredentialManager;